import React, { useCallback, useEffect, useState } from "react";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  DatasetMetaSection,
  MetaAddButton,
} from "@/components/main/datasets/DatasetMetaSection";
import { Select } from "components/common/Select";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  listOrganizations,
  updateDataset,
  type DatasetOrganization,
} from "@/lib/api/datasets";

interface DatasetCreatorsProps {
  datasetId: string;
  /// Assigned creators in display order, owned by the parent row.
  creators: DatasetOrganization[];
  canEdit: boolean;
  onChange: (next: DatasetOrganization[]) => void;
  /// Bumped by the Manage-organisations dialog so the picker re-reads the
  /// registry after an entry is added or corrected.
  registryVersion?: number;
  pr?: string;
}

/// WHO authored this dataset, picked from the shared registry. Creation and
/// correction live in Manage organisations (on the datasets page footer), not
/// here — this section only assigns, so one body can't be typed two ways.
export const DatasetCreators: React.FC<DatasetCreatorsProps> = ({
  datasetId,
  creators,
  canEdit,
  onChange,
  registryVersion = 0,
  pr,
}) => {
  const [registry, setRegistry] = useState<DatasetOrganization[]>([]);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    listOrganizations()
      .then((r) => {
        if (!cancelled) setRegistry(r);
      })
      .catch(() => {
        /* the picker just stays empty; Manage organisations reports errors */
      });
    return () => {
      cancelled = true;
    };
  }, [canEdit, registryVersion]);

  const persist = useCallback(
    async (next: DatasetOrganization[]) => {
      setSaving(true);
      try {
        await updateDataset(datasetId, {
          creator_org_ids: next.map((o) => o.id),
        });
        onChange(next);
      } catch {
        showNotificationGlobal("Failed to save creators", "error");
      } finally {
        setSaving(false);
      }
    },
    [datasetId, onChange],
  );

  const unassigned = registry.filter(
    (o) => !creators.some((c) => c.id === o.id),
  );

  return (
    <DatasetMetaSection
      icon={<CorporateFareOutlinedIcon className="icon-sm" />}
      label="Creator"
      pr={pr}
      itemCount={creators.length}
      action={
        canEdit && !picking && unassigned.length > 0 ? (
          <MetaAddButton
            label="Add creator"
            onClick={() => setPicking(true)}
            disabled={saving}
          />
        ) : undefined
      }
    >
      {creators.length > 0 || picking ? (
        <div className="flex flex-wrap items-center gap-2">
          {creators.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 min-h-[var(--control-height-sm)] px-2 rounded-app border border-border text-fg"
              style={{ fontSize: "var(--font-size-small)" }}
              title={c.same_as ?? c.url ?? c.name}
            >
              {c.name}
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove ${c.name}`}
                  className="flex items-center text-fg-muted hover:text-error cursor-pointer bg-transparent border-0 p-0"
                  disabled={saving}
                  onClick={() =>
                    void persist(creators.filter((x) => x.id !== c.id))
                  }
                >
                  <CloseIcon className="icon-xs" />
                </button>
              )}
            </span>
          ))}
          {picking && (
            <Select
              value=""
              onChange={(id) => {
                const picked = registry.find((o) => o.id === id);
                setPicking(false);
                if (picked) void persist([...creators, picked]);
              }}
              options={unassigned.map((o) => ({
                value: o.id,
                label: `${o.name} · ${o.entity_type}`,
              }))}
              placeholder="Pick an organisation…"
              ariaLabel="Pick a creator"
              size="sm"
              autoFocus
            />
          )}
        </div>
      ) : undefined}
    </DatasetMetaSection>
  );
};
